/**
 * CopyFromSource — copy a section or table word for word from any source
 * document into the current section. No model: the server reads the file
 * (exactly, for Word) or the library's extraction (other files) and proposes
 * the text for review.
 *
 *   step 1  which document      (Word files marked "exact")
 *   step 2  which section/table (the document's own outline)
 *   step 3  where it goes       (in place of the section's text, or after the
 *                                paragraph at the cursor)
 *
 * The result opens in the review window; Word changes only on Approve.
 */

import { makeStyles, mergeClasses, Spinner } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  type CopyFromSourceBody,
  type CopyFromSourceResponse,
  copySectionFromSource,
  getSourceOutline,
  type SourceOutline,
} from "../../api/client";
import { getCursorParagraph } from "../../services/textSelection";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

export interface CopySourceDoc {
  id: string;
  name: string;
}

export type CopyPlacement = "replace_section" | "after_text" | "replace_text";

export interface CopyFromSourceProps {
  runId: string;
  /** The run's module id of the section the copy goes into. */
  moduleId: string;
  /** Its number as shown ("2.6"). */
  sectionNumber: string;
  sectionTitle: string;
  documents: readonly CopySourceDoc[];
  initialPlacement?: CopyPlacement;
  /** A proposal was made — the host opens the review window. */
  onDone: (resp: CopyFromSourceResponse) => void;
  onCancel: () => void;
  /** The text the writer highlighted in Word. When set, the copy replaces
   *  exactly that text (any section of any source; no proposal is made — the
   *  host reviews ``resp.preview`` and applies it to the highlight). */
  highlightText?: string | null;
}

type Pick =
  | { kind: "section"; number: string; title: string }
  | { kind: "table"; index: number | null; caption: string };

export const isWordFile = (name: string): boolean => /\.docx$/i.test(name);

/** Word files first (exact copy), then the rest; within each, names with the
 *  newest year first, then by name. */
export function orderDocuments(docs: readonly CopySourceDoc[]): CopySourceDoc[] {
  const year = (n: string) => Math.max(0, ...(n.match(/(?:19|20)\d\d/g) ?? []).map(Number));
  return docs
    .slice()
    .sort(
      (a, b) =>
        Number(isWordFile(b.name)) - Number(isWordFile(a.name)) ||
        year(b.name) - year(a.name) ||
        a.name.localeCompare(b.name),
    );
}

/** The outline entry with the draft section's number and title, if any —
 *  preselected when the copy replaces the section. */
export function matchingSection(
  outline: SourceOutline,
  number: string,
  title: string,
): { number: string; title: string } | null {
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const byBoth = outline.sections.find((s) => s.number === number && norm(s.title) === norm(title));
  const byTitle = outline.sections.filter((s) => norm(s.title) === norm(title));
  return (
    byBoth ?? (byTitle.length === 1 ? byTitle[0] : null) ?? outline.sections.find((s) => s.number === number) ?? null
  );
}

export function copyBody(
  doc: CopySourceDoc,
  pick: Pick,
  placement: CopyPlacement,
  anchor?: string,
): CopyFromSourceBody {
  const base = { doc_id: doc.id, doc_name: doc.name, placement, anchor_text: anchor };
  if (pick.kind === "table") {
    return pick.index !== null
      ? { ...base, kind: "table", table_index: pick.index }
      : { ...base, kind: "table", table_caption: pick.caption };
  }
  return { ...base, kind: "section", source_number: pick.number || undefined, source_title: pick.title };
}

const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 50,
    margin: 0,
    padding: 0,
    border: "none",
    width: "100%",
    height: "100%",
    maxWidth: "none",
    maxHeight: "none",
    boxSizing: "border-box",
    backgroundColor: "rgba(15, 10, 30, 0.45)",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    fontFamily: fonts.body,
  },
  sheet: {
    width: "100%",
    maxWidth: "560px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: surfaceColors.paper,
  },
  head: { padding: "14px 16px 10px", borderBottom: `1px solid ${surfaceColors.line}` },
  step: { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.04em", color: inkColors.ink4 },
  title: { fontSize: "16px", fontWeight: 600, color: inkColors.ink, marginTop: "2px" },
  sub: { fontSize: "12.5px", color: inkColors.ink3, marginTop: "4px", lineHeight: 1.45 },
  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  filter: {
    height: "32px",
    padding: "0 10px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    fontFamily: fonts.body,
    fontSize: "13px",
    marginBottom: "6px",
  },
  option: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    textAlign: "left",
    padding: "8px 10px",
    borderRadius: "8px",
    border: `1px solid ${surfaceColors.line}`,
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    fontFamily: fonts.body,
    color: inkColors.ink,
  },
  optionOn: { border: `1px solid ${brandColors.primary}`, boxShadow: `0 0 0 1px ${brandColors.primary}` },
  optionText: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  optionName: { fontSize: "13px", fontWeight: 500, overflowWrap: "anywhere" },
  optionMeta: { fontSize: "12px", color: inkColors.ink3 },
  num: { fontFamily: fonts.mono, fontSize: "12px", color: inkColors.ink3, minWidth: "40px", paddingTop: "1px" },
  tabs: { display: "flex", gap: "4px", marginBottom: "6px" },
  tab: {
    height: "28px",
    padding: "0 12px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.line}`,
    background: "none",
    fontFamily: fonts.body,
    fontSize: "12.5px",
    color: inkColors.ink3,
    cursor: "pointer",
  },
  tabOn: {
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink,
    fontWeight: 600,
    border: `1px solid ${surfaceColors.lineStrong}`,
  },
  note: { fontSize: "12px", color: inkColors.ink3, lineHeight: 1.45 },
  error: { fontSize: "12.5px", color: "#A3202E", lineHeight: 1.45 },
  foot: {
    display: "flex",
    gap: "8px",
    padding: "12px 16px",
    borderTop: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paperDim,
  },
  spacer: { flex: 1 },
  btn: {
    height: "34px",
    padding: "0 14px",
    borderRadius: "6px",
    border: `1px solid ${surfaceColors.lineStrong}`,
    backgroundColor: surfaceColors.paper,
    color: inkColors.ink2,
    fontFamily: fonts.body,
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    ":disabled": { opacity: 0.5, cursor: "default" },
  },
  primary: { border: `1px solid ${brandColors.primary}`, backgroundColor: brandColors.primary, color: "#FFFFFF" },
  loading: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12.5px", color: inkColors.ink3 },
});

export const CopyFromSource: React.FC<CopyFromSourceProps> = ({
  runId,
  moduleId,
  sectionNumber,
  sectionTitle,
  documents,
  initialPlacement = "replace_section",
  onDone,
  onCancel,
  highlightText = null,
}) => {
  const styles = useStyles();
  const docs = useMemo(() => orderDocuments(documents), [documents]);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [doc, setDoc] = useState<CopySourceDoc | null>(null);
  const [outline, setOutline] = useState<SourceOutline | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"section" | "table">("section");
  const [filter, setFilter] = useState("");
  const [pick, setPick] = useState<Pick | null>(null);
  const [placement, setPlacement] = useState<CopyPlacement>(highlightText ? "replace_text" : initialPlacement);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!doc || step !== 2) return;
    let live = true;
    setLoading(true);
    setError(null);
    setOutline(null);
    getSourceOutline(runId, doc.id)
      .then((o) => {
        if (!live) return;
        setOutline(o);
        const same = initialPlacement === "replace_section" ? matchingSection(o, sectionNumber, sectionTitle) : null;
        setPick(same ? { kind: "section", number: same.number, title: same.title } : null);
      })
      .catch((err) => live && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [doc, step, runId, initialPlacement, sectionNumber, sectionTitle]);

  const q = filter.trim().toLowerCase();
  const shownDocs = q ? docs.filter((d) => d.name.toLowerCase().includes(q)) : docs;
  const shownSections = (outline?.sections ?? []).filter(
    (s) => !q || `${s.number} ${s.title}`.toLowerCase().includes(q),
  );
  const shownTables = (outline?.tables ?? []).filter((t) => !q || t.caption.toLowerCase().includes(q));

  const submit = async () => {
    if (!doc || !pick) return;
    setBusy(true);
    setError(null);
    try {
      let anchor: string | undefined;
      if (placement === "replace_text") {
        anchor = highlightText ?? undefined;
      } else if (placement === "after_text") {
        const cursor = await getCursorParagraph();
        if (!cursor?.text) {
          setError("Put your cursor in a paragraph of this section in Word, then try again.");
          return;
        }
        if (cursor.sectionNumber && cursor.sectionNumber !== sectionNumber) {
          setError(`Your cursor is in §${cursor.sectionNumber}. Put it in §${sectionNumber}, then try again.`);
          return;
        }
        anchor = cursor.text;
      }
      const resp = await copySectionFromSource(runId, moduleId, copyBody(doc, pick, placement, anchor));
      if (placement === "replace_text" || (placement === "replace_section" && resp.preview)) {
        if (!resp.preview) {
          setError("Nothing came back from that source. Pick another section or document.");
          return;
        }
        onDone(resp);
        return;
      }
      if (resp.redraft) {
        onDone(resp);
        return;
      }
      if (!resp.proposal) {
        setError(
          `§${sectionNumber} already has exactly this text in Asthra, so there is nothing to change. Pick another section, or check that your cursor is in the section you mean.`,
        );
        return;
      }
      onDone(resp);
    } catch (err) {
      const e = err as { detail?: { message?: string } | string };
      const detail = typeof e?.detail === "object" ? e.detail?.message : undefined;
      setError(detail ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  const pickLabel = pick?.kind === "table" ? pick.caption : pick ? `${pick.number} ${pick.title}`.trim() : "";

  return (
    <dialog
      open
      className={styles.backdrop}
      aria-modal="true"
      aria-label="Copy from a source"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.head}>
          <div className={styles.step}>
            Copy from a source · step {step} of 3
            {highlightText
              ? " · into the text you highlighted"
              : sectionNumber
                ? ` · into §${sectionNumber}${sectionTitle ? ` ${sectionTitle}` : ""}`
                : ""}
          </div>
          {step === 1 ? (
            <>
              <div className={styles.title}>Which document?</div>
              <div className={styles.sub}>
                The text is copied exactly as written, without rewriting. Word files are copied exactly; other files
                give the text as it was extracted.
              </div>
            </>
          ) : step === 2 ? (
            <>
              <div className={styles.title}>What should be copied?</div>
              <div className={styles.sub}>{doc?.name}</div>
            </>
          ) : (
            <>
              <div className={styles.title}>Where should it go?</div>
              <div className={styles.sub}>
                {pickLabel} from {doc?.name}
              </div>
            </>
          )}
        </div>

        <div className={styles.body}>
          {step === 1 ? (
            <>
              {docs.length > 6 ? (
                <input
                  className={styles.filter}
                  placeholder="Find a document"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Find a document"
                />
              ) : null}
              {shownDocs.length === 0 ? <div className={styles.note}>No documents in the source library.</div> : null}
              {shownDocs.map((d) => (
                <button
                  type="button"
                  key={d.id}
                  className={mergeClasses(styles.option, doc?.id === d.id && styles.optionOn)}
                  aria-pressed={doc?.id === d.id}
                  onClick={() => setDoc(d)}
                >
                  <span className={styles.optionText}>
                    <span className={styles.optionName}>{d.name}</span>
                    <span className={styles.optionMeta}>
                      {isWordFile(d.name) ? "Word file · copied exactly" : "Extracted text"}
                    </span>
                  </span>
                </button>
              ))}
            </>
          ) : null}

          {step === 2 ? (
            loading ? (
              <div className={styles.loading}>
                <Spinner size="extra-tiny" /> Reading {doc?.name}
              </div>
            ) : outline ? (
              <>
                {/* biome-ignore lint/a11y/useSemanticElements: a segmented control, not a form fieldset */}
                <div className={styles.tabs} role="group" aria-label="Copy a">
                  {(["section", "table"] as const).map((k) => (
                    <button
                      type="button"
                      key={k}
                      className={mergeClasses(styles.tab, tab === k && styles.tabOn)}
                      aria-pressed={tab === k}
                      onClick={() => setTab(k)}
                    >
                      {k === "section" ? `Sections (${outline.sections.length})` : `Tables (${outline.tables.length})`}
                    </button>
                  ))}
                </div>
                <input
                  className={styles.filter}
                  placeholder={tab === "section" ? "Find a section" : "Find a table"}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  aria-label="Find"
                />
                {outline.format === "extracted" ? (
                  <div className={styles.note}>
                    Not a Word file: the copy is the text extracted from it. Check table layout before you approve.
                  </div>
                ) : null}
                {tab === "section"
                  ? shownSections.map((s) => {
                      const on = pick?.kind === "section" && pick.number === s.number && pick.title === s.title;
                      return (
                        <button
                          type="button"
                          key={`${s.number}|${s.title}`}
                          className={mergeClasses(styles.option, on && styles.optionOn)}
                          aria-pressed={on}
                          onClick={() => setPick({ kind: "section", number: s.number, title: s.title })}
                          style={{ paddingLeft: `${10 + Math.max(0, s.level - 1) * 12}px` }}
                        >
                          {s.number ? <span className={styles.num}>{s.number}</span> : null}
                          <span className={styles.optionName}>{s.title}</span>
                        </button>
                      );
                    })
                  : shownTables.map((t, i) => {
                      const on = pick?.kind === "table" && pick.caption === t.caption && pick.index === t.index;
                      return (
                        <button
                          type="button"
                          key={`${t.index ?? i}|${t.caption}`}
                          className={mergeClasses(styles.option, on && styles.optionOn)}
                          aria-pressed={on}
                          onClick={() => setPick({ kind: "table", index: t.index, caption: t.caption })}
                        >
                          <span className={styles.optionText}>
                            <span className={styles.optionName}>{t.caption}</span>
                            {t.section ? <span className={styles.optionMeta}>In section {t.section}</span> : null}
                          </span>
                        </button>
                      );
                    })}
              </>
            ) : null
          ) : null}

          {step === 3 ? (
            <>
              {(highlightText
                ? ([
                    [
                      "replace_text",
                      "Replace the text you highlighted",
                      `“${highlightText.length > 120 ? `${highlightText.slice(0, 120)}…` : highlightText}”`,
                    ],
                  ] as const)
                : ([
                    [
                      "replace_section",
                      sectionNumber ? `Replace the text of §${sectionNumber}` : "Replace this section's text",
                      "Everything under the heading is replaced.",
                    ],
                    [
                      "after_text",
                      "After the paragraph at my cursor",
                      sectionNumber ? `Your cursor must be in §${sectionNumber}.` : "Put your cursor in the section.",
                    ],
                  ] as const)
              ).map(([key, label, meta]) => (
                <button
                  type="button"
                  key={key}
                  className={mergeClasses(styles.option, placement === key && styles.optionOn)}
                  aria-pressed={placement === key}
                  onClick={() => setPlacement(key)}
                >
                  <span className={styles.optionText}>
                    <span className={styles.optionName}>{label}</span>
                    <span className={styles.optionMeta}>{meta}</span>
                  </span>
                </button>
              ))}
              <div className={styles.note}>You review the change before anything in Word changes.</div>
            </>
          ) : null}

          {error ? <div className={styles.error}>{error}</div> : null}
        </div>

        <div className={styles.foot}>
          <button type="button" className={styles.btn} onClick={onCancel}>
            Cancel
          </button>
          <span className={styles.spacer} />
          {step > 1 ? (
            <button
              type="button"
              className={styles.btn}
              onClick={() => {
                setFilter("");
                setError(null);
                setStep((s) => (s === 3 ? 2 : 1));
              }}
              disabled={busy}
            >
              Back
            </button>
          ) : null}
          {step < 3 ? (
            <button
              type="button"
              className={mergeClasses(styles.btn, styles.primary)}
              disabled={step === 1 ? !doc : !pick}
              onClick={() => {
                setFilter("");
                setError(null);
                setStep((s) => (s === 1 ? 2 : 3));
              }}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className={mergeClasses(styles.btn, styles.primary)}
              disabled={busy || !pick}
              onClick={() => void submit()}
            >
              {busy ? "Copying" : "Copy for review"}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default CopyFromSource;
